import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, BookMarked, ExternalLink, Wand2 } from "lucide-react";

/**
 * Task #555: Legal-persona Drafting Wizard.
 *
 * 4-step flow (category → jurisdiction → intent → cited skeleton). Every
 * paragraph in the resulting skeleton carries ≥1 citation id; if the
 * backend returns `{ ok: false, code: "no_grounded_sources" }`, the wizard
 * surfaces a "no grounded sources" empty state rather than fabricating
 * language.
 *
 * The wizard is opt-in. The clause-editor entry button only renders when
 *   `effectivePersona === "Legal" && draftingWizardEnabled === true`.
 *
 * Citation overlay (footnote markers + hover popover) is intentionally a
 * follow-up — for the MVP, the skeleton is dropped into the editor as
 * plain headings + "[1] [2]" references and a sources panel below.
 */

type Source = {
  id: number;
  kind: string;
  citation: string;
  title: string;
  jurisdiction: string | null;
  url: string | null;
  excerpt: string | null;
  scope: "house" | "jurisdiction" | "federal" | "category";
};

type SkeletonResponse =
  | {
      ok: true;
      paragraphs: Array<{ heading: string; body: string; citationIds: number[] }>;
      sources: Source[];
    }
  | { ok: false; code: string; message: string; paragraphs: []; sources: [] };

const CATEGORIES = [
  { v: "compensation", l: "Compensation" },
  { v: "termination", l: "Termination" },
  { v: "buyout", l: "Buyout" },
  { v: "nil", l: "NIL" },
  { v: "title_ix", l: "Title IX" },
  { v: "indemnification", l: "Indemnification" },
  { v: "governing_law", l: "Governing Law" },
  { v: "outside_income", l: "Outside Income" },
  { v: "general", l: "General" },
] as const;

const JURISDICTIONS = [
  { v: "", l: "(none — federal only)" },
  { v: "US-CA", l: "California" },
  { v: "US-TX", l: "Texas" },
  { v: "US-FL", l: "Florida" },
  { v: "US-OH", l: "Ohio" },
  { v: "US-MI", l: "Michigan" },
  { v: "US-AL", l: "Alabama" },
  { v: "US-GA", l: "Georgia" },
  { v: "US-NC", l: "North Carolina" },
  { v: "US-PA", l: "Pennsylvania" },
  { v: "US-IL", l: "Illinois" },
  { v: "US-AZ", l: "Arizona" },
] as const;

export interface DraftingWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user accepts a skeleton; receives plain-text body. */
  onAccept?: (body: string, citationIds: number[]) => void;
}

export function DraftingWizard({ open, onOpenChange, onAccept }: DraftingWizardProps) {
  const { getToken } = useAuth();
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]["v"]>("compensation");
  const [jurisdiction, setJurisdiction] = useState("");
  const [intent, setIntent] = useState("");
  const [skeleton, setSkeleton] = useState<SkeletonResponse | null>(null);

  const generate = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const r = await fetch("/api/wizard/skeleton", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          category,
          jurisdiction: jurisdiction || null,
          intent: intent || null,
        }),
      });
      if (!r.ok) throw new Error(`http_${r.status}`);
      return (await r.json()) as SkeletonResponse;
    },
    onSuccess: (data) => {
      setSkeleton(data);
      setStep(3);
    },
  });

  const reset = () => {
    setStep(0);
    setSkeleton(null);
    setIntent("");
  };

  const accept = () => {
    if (!skeleton || !skeleton.ok) return;
    const numbered = new Map<number, number>();
    skeleton.sources.forEach((s, i) => numbered.set(s.id, i + 1));
    const body = skeleton.paragraphs
      .map((p) => {
        const refs = p.citationIds.map((id) => `[${numbered.get(id) ?? "?"}]`).join(" ");
        return `${p.heading}\n\n${p.body} ${refs}`;
      })
      .join("\n\n---\n\n");
    const allCites = Array.from(new Set(skeleton.paragraphs.flatMap((p) => p.citationIds)));
    onAccept?.(body, allCites);
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" /> Drafting wizard
          </DialogTitle>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-4">
            <Label>Clause category</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as typeof category)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Every suggestion will be grounded in your house library, the selected jurisdiction's
              statutes, federal law, and persuasive authority — never an LLM guess.
            </p>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <Label>Jurisdiction</Label>
            <Select value={jurisdiction} onValueChange={setJurisdiction}>
              <SelectTrigger><SelectValue placeholder="Select jurisdiction" /></SelectTrigger>
              <SelectContent>
                {JURISDICTIONS.map((j) => <SelectItem key={j.v || "none"} value={j.v}>{j.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <Label>Drafting intent (optional)</Label>
            <Textarea
              rows={5}
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              placeholder="E.g. Buyout descending from $1.5M (Y1) to $500K (Y3); coach-initiated termination only."
            />
            <p className="text-xs text-muted-foreground">
              Intent is recorded with the skeleton but does not change the cited authorities — those
              come from the database alone.
            </p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {!skeleton ? null : skeleton.ok === false ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <strong>No grounded sources.</strong> {skeleton.message}
              </div>
            ) : (
              <>
                {skeleton.paragraphs.map((p, i) => {
                  const numbered = new Map<number, number>();
                  skeleton.sources.forEach((s, idx) => numbered.set(s.id, idx + 1));
                  return (
                    <div key={i} className="rounded-md border p-3">
                      <div className="font-medium">{p.heading}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{p.body}</p>
                      <div className="mt-2 text-xs text-muted-foreground">
                        Citations: {p.citationIds.map((id) => `[${numbered.get(id) ?? "?"}]`).join(" ")}
                      </div>
                    </div>
                  );
                })}
                <div className="rounded-md border bg-muted/30 p-3">
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
                    <BookMarked className="h-3 w-3" /> Sources
                  </div>
                  <ol className="space-y-1 text-xs">
                    {skeleton.sources.map((s, i) => (
                      <li key={s.id}>
                        <span className="font-mono">[{i + 1}]</span> <strong>{s.citation}</strong> — {s.title}
                        {s.url ? (
                          <a className="ml-1 inline-flex items-center text-primary" href={s.url} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : null}
                        <span className="ml-2 rounded bg-muted px-1 text-[10px]">{s.scope}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 0 && step < 3 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}>Back</Button>
          )}
          {step < 2 && (
            <Button onClick={() => setStep(step + 1)}>Next</Button>
          )}
          {step === 2 && (
            <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
              {generate.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Generate
            </Button>
          )}
          {step === 3 && skeleton?.ok && (
            <>
              <Button variant="outline" onClick={reset}>Start over</Button>
              <Button onClick={accept}>Insert into editor</Button>
            </>
          )}
          {step === 3 && skeleton && !skeleton.ok && (
            <Button variant="outline" onClick={reset}>Start over</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
